import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
  getAddressByGeolocation,
  getAutoCompleteQuery,
} from 'settings/xhr/axiosRequests'

// config values
import {
  __dev__,
  ADDITIONAL_SEARCHBAR_SELECTS,
  apiEndpoints,
  AUTOCOMPLETE_MIN_STRING_FOR_XHR,
  AUTOCOMPLETE_SELECT_WITH_ADDRESS_INDEX,
  BOOKABLE_TYPES,
  DEFAULT_RADIUS,
  entryPointId,
  mappedClusters,
  MAX_NUMBER_OF_QUERY_MATCHES,
  NAVI_TYPES,
  SET_LOCATION_DATA_FOR_SEARCH,
} from 'settings/app/constants'

// component stack
import { SearchBar, Match, MultiSelectRow } from 'components/molecules'
import { SearchHeader } from 'components/organisms'
import Cluster from 'components/molecules/SearchBar/partials/Cluster/Cluster'

// redux
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteLocationQueryParam,
  deleteQueryParam,
} from 'redux/actions/searchActions'

// libraries
import { HeaderConsumer_ } from 'hocs/HeaderProvider/HeaderProvider'
import { useHistory } from 'react-router'
import qs from 'query-string'
import { htmlDecode } from 'settings/app/helpers'
import { TextContext } from 'hocs'

/**
 * search container
 * @param performSearchRequest
 * @param pageType
 * @returns {JSX.Element}
 * @constructor
 */
const Search = ({ performSearchRequest, pageType }) => {
  // hooks &&  redux state
  const history = useHistory()
  const dispatch = useDispatch()
  const textContext = useContext(TextContext)

  // store
  const { bookmarks, application, search } = useSelector((state) => state)
  const { entryPoint, naviType } = application
  const { params, clientHasSearched } = search
  const { totalCount: bookmarkCount } = bookmarks

  // local state
  const { types: bookmarkTypes } = bookmarks
  const [query, setQuery] = useState(params.query)
  const [location, setLocation] = useState(params.location)
  const [key, setKey] = useState(params.key)
  const [type, setType] = useState(params.type)
  const [radius, setRadius] = useState()

  const [searchIsDisabled, setSearchIsDisabled] = useState(false)
  const [distanceAsSorting, setDistanceAsSorting] = useState(false)
  const [queryStringIsValid, setQueryStringIsValid] = useState(false)
  const [locationStringIsValid, setLocationStringIsValid] = useState(false)
  const [locationCoordinates, setLocationCoordinates] = useState({
    lng: false,
    lat: false,
  })
  const [autoCluster, setAutoCluster] = useState({
    query: false,
    location: false,
  })
  const [autoClusterError, setAutoClusterError] = useState({
    type: false, // query or location
    error: false, // location duplication error
    errorMessage: '', // and there shown message
  })
  const [inputs, setInputs] = useState({
    query: false,
    location: false,
  })
  const [showCompareButton, setShowCompareButton] = useState(false)
  const showAdditionalSelects =
    naviType === NAVI_TYPES[1] &&
    entryPoint !== entryPointId.CARESEARCH &&
    entryPoint !== entryPointId.CARESUPPORT
  const [additionalSelects, setAdditionalSelect] = useState({})

  // get additional search bar select and set initial or preset values
  useEffect(() => {
    if (showAdditionalSelects) {
      const selectState = {}
      const currentGetParams = qs.parse(window.location.search)

      // read additional values from => url || set initial values
      if (Object.keys(currentGetParams).length > 0) {
        Object.keys(ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]).forEach(
          (i, k) => {
            selectState[ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k].typ] =
              currentGetParams[ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k].typ]
          }
        )
      } else {
        // set initial values
        Object.keys(ADDITIONAL_SEARCHBAR_SELECTS[entryPoint]).forEach(
          (i, k) => {
            selectState[ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k].typ] =
              ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k]?.preSelect
                ? ADDITIONAL_SEARCHBAR_SELECTS[entryPoint][k]?.preSelect
                : null
          }
        )
      }
      setAdditionalSelect(selectState)
    }
  }, [showAdditionalSelects, entryPoint])

  useEffect(() => {
    setQuery(params.query)
  }, [params.query])

  useEffect(() => {
    setLocation(params.location)
  }, [params.location])

  useEffect(() => {
    setKey(params.key)
  }, [params.key])

  useEffect(() => {
    setType(params.type)
  }, [params.type])

  useEffect(() => {
    setQueryStringIsValid(true)
  }, [entryPoint])

  useEffect(() => {
    setLocationStringIsValid(true)
  }, [entryPoint])

  /**
   * condition for showing compare view button (dep on bookmarks and entryPoint)
   */
  useEffect(() => {
    if (
      !!bookmarkTypes &&
      !!entryPoint &&
      BOOKABLE_TYPES.includes(entryPoint)
    ) {
      setShowCompareButton(bookmarkTypes[entryPoint].count >= 2)
    }
  }, [entryPoint, bookmarkTypes])

  /**
   * set default radius
   * 1. radius from url
   * 2. radius from url (nationwide only location is set)
   * 3. set new radius
   */
  useEffect(() => {
    let newRadius

    if (!!params.radius) {
      newRadius = params.radius
    } else if (!params.radius && !!params.location) {
      newRadius = 0
    } else {
      newRadius = DEFAULT_RADIUS[entryPoint]
    }

    setRadius(newRadius)
  }, [entryPoint, params.radius, params.location])

  /**
   * set the loading state for the input components
   * @param input
   */
  const handleInputState = (input) => {
    setInputs((prevInputs) => ({
      ...inputs,
      [input]: !prevInputs[input],
    }))
  }

  /**
   * set cluster data for initial state
   */
  const resetAutoClusters = () => {
    setAutoCluster({
      query: false,
      location: false,
      error: false,
      errorMessage: '',
    })
  }

  /**
   * renders location matches
   * @returns {*}
   */
  const getLocationAutoCompleteMatches = () =>
    autoCluster.location.map((match, key) => (
      <Match
        key={ key }
        match={ match }
        matchNumber={ key }
        matchClicked={ (match) => setLocationAutoCompleteSelection(match) }
      />
    ))

  /**
   * set user select from autocomplete for location
   * @param match
   */
  const setLocationAutoCompleteSelection = (match) => {
    setLocation(
      `${ match.zip_code } ${ match.name }${ match.street && ` ${ match.street }` }`
    )
    setInputs({
      ...inputs,
      location: false,
    })
    setAutoCluster({
      ...autoCluster,
      location: false,
    })
    setAutoClusterError({
      type: false,
      error: false,
      errorMessage: '',
    })
    setDistanceAsSorting(true)
    setLocationStringIsValid(true)

    // set location coordinates for distance calculation
    if (SET_LOCATION_DATA_FOR_SEARCH.includes(entryPoint)) {
      setLocationCoordinates({ lng: match.lng, lat: match.lat })
    }
  }

  /**
   * return cluster parent by match name
   * @param name
   * @returns {*}
   */
  const getQueryClusterParentByName = (name) =>
    autoCluster.query.find((cluster) =>
      cluster.matches.find((match) => match.source.name === name)
    )

  /**
   * QUERY SELECTION
   * set the user selection from autocomplete cluster based on selected element
   * @param match
   * @param key
   * @param type
   */
  const setQueryAutoCompleteSelection = (match, key, type) => {
    const cluster = getQueryClusterParentByName(match)

    // check if cluster index allow automatic address select
    if (AUTOCOMPLETE_SELECT_WITH_ADDRESS_INDEX.includes(cluster.index)) {
      let location = ''

      // get location from hospital
      cluster.matches.forEach((mat) => {
        if (mat.source.name === match) {
          location = `${ mat.source.address.zip } ${ mat.source.address.city }`
        }
      })

      setQuery(htmlDecode(match))
      setLocation(location)
      setKey(key)
      setType(type)
      setQueryStringIsValid(true)
      setAutoCluster({
        ...autoCluster,
        query: false,
      })
      setAutoClusterError({
        type: false,
        error: false,
        errorMessage: '',
      })
    } else {
      setQuery(htmlDecode(match))
      setKey(key)
      setType(type)
      setQueryStringIsValid(true)
      setAutoCluster({
        ...autoCluster,
        query: false,
      })
      setAutoClusterError({
        type: false,
        error: false,
        errorMessage: '',
      })
    }
  }

  /**
   * handler for input change location
   * @param inputLocation
   */
  const handleLocationChange = (inputLocation) => {
    setLocation(inputLocation)
    setLocationStringIsValid(inputLocation === '')

    // reset duplication error state if exists
    if (autoClusterError.error) {
      setAutoCluster({
        ...autoCluster,
      })
      setAutoClusterError({
        type: false,
        error: false,
        errorMessage: '',
      })
    }

    // perform a request if min require string length has reached
    if (
      AUTOCOMPLETE_MIN_STRING_FOR_XHR[entryPoint].location <=
      inputLocation.trim().length
    ) {
      handleInputState('location')
      setSearchIsDisabled(true)
      getAutoCompleteQuery({
        endPoint: apiEndpoints.locations,
        query: inputLocation.trim(),
      })
        .then((data) => {
          // TODO: check if this is the best solution
          if (
            AUTOCOMPLETE_MIN_STRING_FOR_XHR[entryPoint].location <=
            inputLocation.trim().length
          ) {
            if (0 < data.length) {
              setAutoCluster({
                ...autoCluster,
                location: data.sort((a, b) =>
                  a.source.zip_code.localeCompare(b.source.zip_code)
                ),
              })
            }
            setSearchIsDisabled(false)
          }
          handleInputState('location')
        })
        .catch(() => {
          handleInputState('location')
        })
    } else {
      setAutoCluster({
        ...autoCluster,
        location: false,
      })
    }
  }

  const getQueryErrorMessage = () => {
    if (query.length <= 2) {
      return textContext.autoCompleteQueryToShort
    } else if (query.length >= 3 && !autoCluster.query) {
      return textContext.autoCompleteQueryNotValid
    }
    return textContext.autoCompleteQuerySelectSuggestion
  }

  const getLocationErrorMessage = () => {
    if (location.length <= 2) {
      return textContext.autoCompleteLocationToShort
    } else if (location.length >= 3 && !autoCluster.location) {
      return textContext.autoCompleteLocationNotValid
    }
    return textContext.autoCompleteLocationSelectSuggestion
  }

  /**
   * copies or merges autocomplete Clusters into a new Array for display purposes
   * @param data {object}
   * @returns {object}
   */
  const mergeClusters = (data) => {
    // create new Array from the autoCluster data without any references
    const preMergedClusters = JSON.parse(JSON.stringify(data))
    const mergedClusters = []

    preMergedClusters.forEach((cluster) => {
      if (mappedClusters.hasOwnProperty(cluster.index)) {
        const mClusterIdx = mergedClusters.findIndex(
          (mCluster) => mCluster.index === mappedClusters[cluster.index]
        )
        // prevent adding already merged matches
        if (
          mergedClusters[mClusterIdx].matches.findIndex(
            (match) => match.key === cluster.index
          ) < 0
        ) {
          mergedClusters[mClusterIdx].matches = [
            ...mergedClusters[mClusterIdx].matches,
            ...cluster.matches,
          ]
        }
      } else {
        mergedClusters.push(cluster)
      }
    })

    return mergedClusters
  }

  /**
   * renders query parent cluster and map children into parent
   * @returns {*}
   */
  const getQueryAutoCompleteCluster = () => {
    // create new cluster Array that contains merged clusters for displaying purposes
    const mergedClusters = mergeClusters(autoCluster.query)
    return mergedClusters.map((cluster, key) => (
      <Cluster
        key={ key }
        order={ key }
        title={ cluster.index }
        matchClicked={ (match, key, type) =>
          setQueryAutoCompleteSelection(match, key, type)
        }
        matches={ cluster.matches }
        maxMatches={ MAX_NUMBER_OF_QUERY_MATCHES }
      />
    ))
  }

  /**
   * handler for input change query
   * @param inputQuery
   */
  const handleQueryChange = (inputQuery) => {
    setQuery(inputQuery)
    setKey('')
    setType('')
    setQueryStringIsValid(inputQuery === '')

    if (autoClusterError.error) {
      setAutoClusterError({
        type: false,
        error: false,
        errorMessage: '',
      })
    }

    // perform request if min string length has reached
    if (
      AUTOCOMPLETE_MIN_STRING_FOR_XHR[entryPoint].query <=
      inputQuery.trim().length
    ) {
      handleInputState('query')
      getAutoCompleteQuery({
        endPoint: `${ entryPoint }/`,
        query: inputQuery.trim(),
      })
        .then((data) => {
          if (
            AUTOCOMPLETE_MIN_STRING_FOR_XHR[entryPoint].query <=
            inputQuery.trim().length
          ) {
            if (0 < data.length) {
              setAutoCluster({
                ...autoCluster,
                query: data,
              })
            }
          }
          handleInputState('query')
        })
        .catch(() => {
          handleInputState('query')
        })
    } else if (autoCluster.error) {
      // don't reset auto cluster data of error or location if error exists
      // error needs to be fixed first
      const oldAutoCluster = autoCluster
      resetAutoClusters()
      setAutoCluster({
        ...autoCluster,
        location: oldAutoCluster.location,
      })
    } else {
      setAutoCluster({
        ...autoCluster,
        query: false,
      })

      setInputs({
        ...inputs,
        query: false,
      })
    }
  }

  const validateLocationError = () => {
    let locationDuplications = false

    // search string duplications in location autoCluster (no unique location state)
    if (false !== autoCluster.location) {
      locationDuplications =
        1 <
        autoCluster.location.map((match) => match.highlight.includes(location))
          .length
    }

    // compare last match with current location (equal is valide, not equal is a validation error)
    if (autoCluster.location.length === 1 && !locationDuplications) {
      const currentLocationMatch = autoCluster.location[0].source
      const currentLocationString = `${ currentLocationMatch.zip_code } ${ currentLocationMatch.name } ${ currentLocationMatch.street }`
      locationDuplications = currentLocationString.trim() !== location
    }

    return locationDuplications
  }

  /**
   * desktop search request handler (input validation @ search btn clicked)
   * 1. check if query/location is valide
   * 2. check location for potential errors (duplication or not selected from suggestions)
   *    and no xhrs are pending
   */
  const handleSearchRequest = async () => {
    const autocompleteRequestsPending = inputs.location
    const locationDuplications = validateLocationError()

    // no valid location || query
    if (!locationStringIsValid || !queryStringIsValid) {
      setAutoClusterError({
        type: !locationStringIsValid ? 'location' : 'query',
        error: true,
        errorMessage: !locationStringIsValid
          ? getLocationErrorMessage()
          : getQueryErrorMessage(),
      })
      return false
    }

    // 2. check location for potential errors (duplication or not selected from suggestions) and no xhrs are pending
    if (
      !autocompleteRequestsPending &&
      !locationDuplications &&
      queryStringIsValid
    ) {
      // Reset UI Error State when GeoService Localization is not available (then search without location)
      if (
        autoClusterError.type === 'location' &&
        autoClusterError.errorMessage ===
          textContext.autoCompleteNoGeoServiceMessage
      ) {
        setAutoClusterError({
          type: false,
          error: false,
          errorMessage: '',
        })
      }

      // parsing search params before request
      let params = {
        query,
        location,
        radius: '' === location ? '' : radius, // if no location, then this value is the default prop for radius
        key,
        type,
        lat: locationCoordinates.lat,
        lng: locationCoordinates.lng,
      }

      // append additional search params (some search types has more params care navigator)
      if (showAdditionalSelects) {
        params = { ...params, ...additionalSelects }
      }

      // set distance as current sorting
      if (distanceAsSorting) {
        params.sorting = 'distance'
      }

      await performSearchRequest(params)
      resetAutoClusters()

      return Promise.resolve()
    }

    // set location error
    if (locationDuplications) {
      setAutoCluster({
        ...autoCluster,
      })
      setAutoClusterError({
        type: 'location',
        error: true,
        errorMessage: textContext.autoCompleteLocationSelectSuggestion,
      })
    }
  }

  /**
   * perform get address data from api and set state
   * @param position
   */
  const receivePositionData = (position) => {
    handleInputState('location')

    getAddressByGeolocation({
      lat: position.coords.latitude,
      lng: position.coords.longitude,
    })
      .then((address) => {
        setLocation(`${ address.postal_code } ${ address.city }`)
        setInputs({
          ...inputs,
          location: false,
        })
        setSearchIsDisabled(false)
        setDistanceAsSorting(true)
      })
      .catch(() => {
        handleInputState('location')
        setSearchIsDisabled(false)
        setDistanceAsSorting(false)
        setAutoClusterError({
          type: 'location',
          error: true,
          errorMessage: textContext.autoCompleteNoGeoServiceMessage,
        })
      })
  }

  /**
   * get user location
   */
  const locateUserWithBrowserSupport = () => {
    resetAutoClusters()
    setSearchIsDisabled(!searchIsDisabled)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(receivePositionData)
    }
  }

  /**
   * handle delete button event
   * @param input
   */
  const deleteButtonClicked = (input) => {
    if ('query' === input) {
      // locale state
      setQuery('')
      setKey('')
      setType('')
      setQueryStringIsValid(true)
      setAutoCluster({
        ...autoCluster,
        query: false,
      })
      setAutoClusterError({
        type: false,
        error: false,
        errorMessage: '',
      })

      // redux state
      dispatch(deleteQueryParam())
    }
    if ('location' === input) {
      // locale state
      setLocation('')
      setRadius(DEFAULT_RADIUS[entryPoint])
      setLocationStringIsValid(true)
      setAutoCluster({
        ...autoCluster,
        location: false,
      })
      setAutoClusterError({
        type: false,
        error: false,
        errorMessage: '',
      })
      setDistanceAsSorting(false)
      setLocationCoordinates({ lng: false, lat: false })
      dispatch(deleteLocationQueryParam(DEFAULT_RADIUS[entryPoint]))
    }
  }

  /**
   * check the query error state
   * @returns {Promise<void>|void}
   */
  const handleQueryEnter = () =>
    new Promise((resolve) => {
      if (!queryStringIsValid) {
        setAutoClusterError({
          type: 'query',
          error: true,
          errorMessage: getQueryErrorMessage(),
        })
        return
      }

      setAutoCluster({
        ...autoCluster,
        query: false,
      })

      return resolve()
    })

  /**
   * check the location error state
   * @returns {Promise<void>|void}
   */
  const checkLocationErrorState = () =>
    new Promise((resolve) => {
      const autocompleteRequestsPending = inputs.location
      const locationDuplications = validateLocationError()

      // autocomplete has matches but nothing is selected
      if (locationDuplications) {
        setAutoCluster({
          ...autoCluster,
        })
        setAutoClusterError({
          type: 'location',
          error: true,
          errorMessage: textContext.autoCompleteLocationSelectSuggestion,
        })
        return
      }

      // invalid search string
      if (!locationStringIsValid) {
        setAutoClusterError({
          type: 'location',
          error: true,
          errorMessage: getLocationErrorMessage(),
        })
        return
      }

      if (
        !autocompleteRequestsPending &&
        !locationDuplications &&
        locationStringIsValid
      ) {
        return resolve()
      }
    })

  /**
   * exec "enter" handling only if location autocomplete results exist
   */
  const handleLocationEnter = () => {
    if (autoCluster.location) {
      const [firstMatch] = autoCluster.location
      if (!!firstMatch.source) {
        setLocationAutoCompleteSelection(firstMatch.source)
        setLocationStringIsValid(true)
      }
    }
  }

  const getAdditionalSelect = () => (
    <MultiSelectRow
      selectedValues={ additionalSelects }
      selectData={ ADDITIONAL_SEARCHBAR_SELECTS[entryPoint] }
      onAdditionalSelectChange={ (typ, value) =>
        setAdditionalSelect({
          ...additionalSelects,
          [typ]: value,
        })
      }
    />
  )

  return (
    <HeaderConsumer_>
      {(header) => (
        <SearchHeader
          headerImage={
            __dev__
              ? !clientHasSearched && header.headerImage
              : header.headerImage
          }
          headerHeadline={ header.textTitle }
          headerText={ header.text }
          title={ header.headerTitle }
          bookmarkCount={ bookmarkCount }
          showCompareButton={ showCompareButton }
          onBookmarkButtonClicked={ () =>
            history.push({
              pathname: '/merkliste',
              state: { lastEntryPoint: entryPoint },
            })
          }
          searchBar={
            <SearchBar
              // input handler
              onChangeQuery={ (inputQuery) => handleQueryChange(inputQuery) }
              onChangeLocation={ (inputLocation) =>
                handleLocationChange(inputLocation)
              }
              onChangeRadius={ (inputRadius) => setRadius(inputRadius) }
              onDeleteButtonClicked={ (input) => deleteButtonClicked(input) }
              onQueryEnter={ () => handleQueryEnter() }
              onLocationEnter={ () => handleLocationEnter() }
              checkLocationErrorState={ () => checkLocationErrorState() }
              // search handler (actions)
              onSearchButtonClicked={ () => handleSearchRequest() }
              onLocateButtonClicked={ () => locateUserWithBrowserSupport() }
              searchButtonDisabled={ searchIsDisabled }
              // query params
              query={ query }
              location={ location }
              radius={ radius }
              search={{ key, type }}
              locationCoordinates={ locationCoordinates }
              // autocomplete data
              renderQueryOptions={
                !autoCluster.query ? false : getQueryAutoCompleteCluster()
              }
              renderLocationOptions={
                !autoCluster.location ? false : getLocationAutoCompleteMatches()
              }
              // input states (loader && error)
              searchLoading={ inputs.query }
              locationLoading={ inputs.location }
              autoClusterError={ autoClusterError }
              pageType={ pageType }
              // additional selects for search (ABS2)
              additionalSelects={
                showAdditionalSelects ? getAdditionalSelect() : false
              }
              additionalSelectsValues={ additionalSelects }
            />
          }
        />
      )}
    </HeaderConsumer_>
  )
}

Search.propTypes = {
  performSearchRequest: PropTypes.func.isRequired,
  pageType: PropTypes.string,
}

export default Search
